/**
 * Pure Final Jeopardy helpers over the shared domain types (FR-6.4).
 *
 * Lives in `shared` (not game-server) because BOTH sides need them: the actor
 * computes the judging order + drives the sequence, and the Host UI derives the
 * "who's up next to judge" prompt from the same logic — one definition, no drift
 * (AGENT_GUIDE §5). No I/O, fully unit-testable.
 */

import type { Player } from './domain';

/**
 * The order the host judges players in: ascending by score **at the start of
 * Final Jeopardy** (lowest first — FR-6.4). Ties break deterministically by
 * `seatId` so the order is stable and reproducible (recovery-safe, M4). Returns
 * the player ids in judging order; each participant appears once.
 */
export function finalJudgingOrder(players: Player[]): string[] {
  return [...players]
    .sort((a, b) =>
      a.score !== b.score ? a.score - b.score : a.seatId.localeCompare(b.seatId),
    )
    .map((p) => p.id);
}

/**
 * The next player to judge: the first id in `judgingOrder` not yet in
 * `judgedPlayerIds`, or `null` when everyone has been judged (→ GAME_OVER).
 * Enforces the strict lowest-first sequence — the host cannot skip ahead.
 */
export function nextFinalJudgePlayerId(
  judgingOrder: string[],
  judgedPlayerIds: string[],
): string | null {
  return judgingOrder.find((id) => !judgedPlayerIds.includes(id)) ?? null;
}

/**
 * True once every participant has submitted the given secret (wager or answer).
 * `submittedIds` are the keys present; `participantIds` is the expected roster.
 * An empty roster is trivially complete.
 */
export function allFinalSubmitted(
  participantIds: string[],
  submittedIds: string[],
): boolean {
  return participantIds.every((id) => submittedIds.includes(id));
}
